<template>
  <v-app class="premium-app">
    <v-progress-linear 
      v-if="loading"
      indeterminate
      class="premium-global-loader"
      color="primary"
      height="3"
    />

    <v-app-bar
      :style="[
        loading ? 'margin-top: 3px;' : '',
        'transition: all 0.5s ease-in-out',
        'background-color: var(--app-color-background);'
      ]"
      v-if="smAndDown"
    >
      <template #prepend>
        <img
          src="@/assets/images/logo.png" 
          alt="Conduril logo in white" 
          srcset=""  
          height="40px"      
          class="px-4"  
        />
      </template>

      <template v-slot:append>
        <v-app-bar-nav-icon
          @click="openMenu"
          :icon="!showMenu ? 'mdi-menu' : 'mdi-close'"
        ></v-app-bar-nav-icon>
      </template>
    </v-app-bar>

    <v-navigation-drawer
      class="premium-drawer"
      v-model="showMenu"
      :rail="(mdAndDown || showMini) && !smAndDown"
      :width="250"
      :rail-width="64"
      location="left"
      elevation="6"
      floating
      :permanent="mdAndUp"
      :expand-on-hover="smAndUp"
    >
      <div class="drawer-content d-flex flex-column">
        <img 
          v-if="!smAndDown"
          src="@/assets/images/logo.png" 
          alt="Conduril logo" 
          srcset=""
          class="sidebar-logo"
        >

        <div class="premium-navigation flex-grow-1 d-flex flex-column">
          <template v-if="loading">
            <div class="nav-skeleton flex-grow-1">
              <v-skeleton-loader
                v-for="i in 6"
                :key="i"
                color="dark"
                type="list-item"
                class="skeleton-item"
              />
            </div>
          </template>

          <v-list 
            v-else
            v-model:opened="openMenus"
            prepend-gap="1rem"
            indent="2rem"
            nav 
            class="premium-nav-list flex-grow-1" 
          >
            <template v-for="item in items" :key="item.title">
              <v-list-item 
                class="premium-nav-item" 
                link 
                :to="item.link" 
                :exact="item.exact"
                :prepend-icon="item.icon"
                :title="showMenu ? item.title : ''"
                v-if="item.items == 0"
                style="border-radius: 10px;"
              >
              </v-list-item>  

              <v-list-group
                :value="item.group"
                class="premium-nav-group" 
                style="border-radius: 10px;"
                v-else
              >
                <template v-slot:activator="{ props }">
                  <v-list-item 
                    class="premium-nav-item group-header" 
                    v-bind="props"
                    :prepend-icon="item.icon"
                    :title="showMenu ? item.title : ''"
                    @click="() => {
                      showMini = false;
                      openMenus.push(item.title);
                    }"
                    style="border-radius: 10px;"
                  />
                </template>
                
                <template v-for="child in item.items" :key="child.title">
                  <v-list-item 
                    class="premium-nav-item sub-item" 
                    link 
                    :to="child.link" 
                    :exact="child.exact"
                    :prepend-icon="child.icon"
                    :title="showMenu ? child.title : ''"
                    v-if="child.has_permission"
                    style="border-radius: 10px;"
                  >
                  </v-list-item>
                </template>
              </v-list-group>
            </template>
          </v-list>

          <v-list>
            <v-list-item 
              class="premium-nav-item"
              link 
              to="" 
              :exact="true"
              prepend-icon="mdi-power"
              :title="showMenu ? 'Terminar Sessão' : ''"
              @click.prevent="logout"
              style="padding-block: 2rem;"
            >
            </v-list-item>
          </v-list>
        </div>
      </div>
    </v-navigation-drawer>

    <v-main class="premium-main">
      <v-alert
        v-if="sessionError"
        type="error"
        variant="tonal"
        :title="sessionErrorTitle"
        :text="sessionError"
        closable
      >
      </v-alert>
      
      <div
        class="premium-content-container"
        v-if="!loading"
      >
        <router-view />
      </div>
    </v-main>

    <v-footer class="premium-footer flex-0-0">
      <div class="footer-content">
      </div>
    </v-footer>

    <UniversalDialog>
      <template #headerTitle>
        <span></span>
      </template>

      <template #footer="{ options, isLoading, confirm }">
        <v-alert
          v-if="options.onConfirmFailed"
          type="error"
          :title="options.onConfirmFailedErrorTitle"
          :text="options.onConfirmFailedErrorMessage"
          variant="outlined"
          class="ma-4"
        />
        <v-divider v-if="options.showActions" />
        <v-card-actions
          v-if="options.showActions"
          class="pa-4 justify-center ga-4"
        >
          <v-btn
            :color="options.confirmActionColor"
            :variant="options.confirmActionVariant"
            :prepend-icon="
              isLoading
                ? options.onConfirmActionIcon
                : options.confirmActionIcon
            "
            :class="[
              options.confirmActionClasses,
              'w-75',
            ]"
            @click="confirm"
            :disabled="isLoading"
          >
            {{
              isLoading
                ? options.onConfirmActionText
                : options.confirmActionText
            }}
          </v-btn>
        </v-card-actions>
      </template>
    </UniversalDialog>
  </v-app>
</template>

<script setup>
import {
  ref,
  reactive,
  onMounted,
  watch,
  getCurrentInstance,
  computed,
  onBeforeMount,
} from 'vue'

import {
  useRouter,
  useRoute,
} from 'vue-router'

import { useTheme } from 'vuetify'
import { useDisplay } from 'vuetify'

import User from '@/api/User.js'
import { useSessionStore } from '@/stores/SessionStore'

import UniversalDialog from '@internal_projects_dd/universal-dialog'

onBeforeMount(() => {
  document.title = 'Nexus'
})

const router = useRouter()
const route = useRoute()
const theme = useTheme()
const instance = getCurrentInstance()

const sessionError = ref(null)
const sessionErrorTitle = ref(null)

const themes = instance.appContext.config.globalProperties.$themes

const showMenu = ref(true)
const showMini = ref(true)
const user = reactive({})
const session = ref(null)
const items = ref([])
const loading = ref(true)
const rotateAvatar = ref(false)
const hover = ref(false)
const canRotate = ref(true)

const openMenus = ref([])

const {
  width,
  lgAndUp,
  lgAndDown,
  mdAndDown,
  mdAndUp,
  smAndDown,
  smAndUp,
} = useDisplay()

const routesForBreadCrumbs = computed(() => {
  const crumbs = []

  const isLayoutRoute = (routeRecord) => {
    return routeRecord?.components?.default?.__name?.includes('Layout')
      || routeRecord?.components?.default?.__file?.includes('Layout.vue')
  }

  route.matched.forEach((matchedRoute) => {
    if (!isLayoutRoute(matchedRoute)) {
      crumbs.push(matchedRoute)
    } else if (!matchedRoute?.name) {
      const basePathFromChildren = matchedRoute?.children?.filter(
        (childRoute) => {
          if (
            childRoute?.path === ''
            && childRoute?.name
          ) {
            return {
              ...childRoute,
              path: matchedRoute.path,
            }
          }

          return false
        }
      )

      crumbs.splice(-1, 0, ...basePathFromChildren)
    }
  })

  return crumbs
})

watch(showMenu, (value) => {
  localStorage['menu-show'] = value
}, {
  immediate: false,
})

watch(showMini, (value) => {
  localStorage['menu-mini'] = value

  if (showMini.value) {
    openMenus.value = []
  }
}, {
  immediate: false,
})

watch(route, () => {
  if (!session.value?.theme) {
    return
  }

  let currentTheme = session.value.theme

  currentTheme = currentTheme.length === 0
    ? themes[0]
    : currentTheme

  setTheme(currentTheme)
  setCssVars()
}, {
  immediate: true,
})

const startRotation = () => {
  if (canRotate.value) {
    hover.value = true
    rotateAvatar.value = true
    canRotate.value = false

    setTimeout(() => {
      canRotate.value = true
    }, 60000)
  }
}

const stopRotation = () => {
  hover.value = false
  rotateAvatar.value = false
}

const getVersion = () => {
  return process.env.VUE_APP_SECRET_VERSION
}

const setCssVars = () => {
}

const setTheme = (themeConfig) => {
  if (!themeConfig || !theme?.themes?.value) {
    return
  }
  
  const dark = themeConfig.dark
  const light = themeConfig.light
  
  Object.keys(dark).forEach((key) => { 
    if (theme.themes.value.dark.colors) {
      theme.themes.value.dark.colors[key] = dark[key] 
    }
  })

  Object.keys(light).forEach((key) => { 
    if (theme.themes.value.light.colors) {
      theme.themes.value.light.colors[key] = light[key] 
    }
  })
}

const toggleTheme = () => {
  const newTheme = theme.global.name.value === 'dark'
    ? 'light'
    : 'dark'

  theme.global.name.value = newTheme
  localStorage['dark-mode'] = newTheme === 'dark' ? 1 : 0
}

/*
 * O botão "Terminar Sessão" faz somente redirect
 * para o portal configurado no ambiente.
 *
 * Não termina sessão no Keycloak.
 * Não chama API.
 * Não limpa storage.
 * Não usa fallback.
 */
const logout = () => {
  window.location.href = process.env.VUE_APP_LOGIN_PORTAL_URL + 'dashboard'
}

const openMenu = () => {
  const isMobile = window.innerWidth < 1500
  
  if (!isMobile) {
    showMini.value = !showMini.value
    return
  }

  showMini.value = false
  showMenu.value = !showMenu.value
}

const setMenu = () => {
  if (
    !session.value?.hasPermission
    || !session.value?.hasRole
  ) {
    return
  }

  const menu = [
    {
      title: 'Dashboard',
      icon: 'mdi-view-dashboard',
      has_permission: true,
      exact: true,
      link: '/admin',
      items: [],
    },
    {
      title: 'Mudança de base de trabalho',
      icon: 'mdi-home-switch-outline',
      has_permission: session.value.hasPermission([
        "user.department.membership.request.list.any",
        "user.department.membership.request.list.department",
        // "user.department.membership.request.list.self"
      ]),
      exact: true,
      link: '/departments/membership-requests',
      items: [],
    },
    {
      title: "Férias",
      icon: "mdi-chat-sleep",
      has_permission: session.value.hasPermission([
        "absence.request.create.any",
        "absence.request.create.department",
      ]),
      exact: true,
      link: "/absences/requests",
      items: [],
    },
    {
      title: "Exportações",
      icon: "mdi-download",
      has_permission: session.value.hasPermission([
        "user.department.membership.request.export.any",
        "user.department.membership.request.export.department",
      ]),
      exact: true,
      link: "/exports",
      items: [],
    },
  ]

  items.value = menu.filter((item) => {
    return item.has_permission
  })
}

onMounted(() => {
  theme.global.name.value = +localStorage['dark-mode']
    ? 'dark'
    : 'light'

  User.getSession(false)
    .then(({ data }) => {
      const userInstance = User.getInstance(data)

      Object.assign(user, userInstance)

      session.value = userInstance

      const sessionStore = useSessionStore()

      sessionStore.user = userInstance

      if (
        instance?.appContext
          ?.config
          ?.globalProperties
          ?.$root
      ) {
        instance.appContext.config.globalProperties.$session = userInstance
        sessionStore.user = userInstance
      }

      let currentTheme = data?.theme || []

      currentTheme = currentTheme.length === 0
        ? themes[0]
        : currentTheme

      setTheme(currentTheme)
      setMenu()

      loading.value = false

      setCssVars()
    })
    .catch((error) => {
      console.error(error)

      loading.value = false

      sessionError.value = error?.response?.data?.error
        || error?.response?.data?.message
        || error?.message
        || 'Não foi possível carregar a sessão da aplicação.'

      sessionErrorTitle.value = `${
        error?.name
          ? error.name
          : 'Erro de sessão'
      }${
        error?.code
          ? ` (code: "${error.code}")`
          : ''
      }:`
    })

  showMini.value = localStorage['menu-mini'] === 'true'

  const showMenuStorage = localStorage['menu-show']

  showMenu.value = showMenuStorage !== undefined
    ? showMenuStorage === 'true'
    : true

  showMenu.value = showMenu.value || window.innerWidth >= 960

  rotateAvatar.value = false
  canRotate.value = true
})

function debugConfirm(confirm) {
  alert('Clique no confirmar do Layout UniversalDialog')
  console.log('Clique no confirmar do Layout UniversalDialog')
  confirm()
}
</script>
